import { cloneDeep, isEmpty as _isEmpty, isNumber } from "lodash";
import { FACEBOOK_CTA_IDS } from "social/constants/CTA";
import { DYNAMIC_SOCIAL_AD_TYPES, SOCIAL_AD_TYPES, SOCIAL_FORMATS } from "utils/constants";

export function isEmpty(value) {
	if (typeof value === "string") { // Check for empty strings
		return value.trim().length === 0;
	}

	if (isNumber(value) || typeof value === "boolean") { // Allow numbers and booleans
		return false;
	}

	return _isEmpty(value); // Use lodash utility for objects
}

export function defaultSanitizer(values) {
	return Object.keys(values).reduce((prev, key) => {
		const value = values[key];
		if (isEmpty(value)) {
			return {
				...prev,
				[key]: null,
			};
		}

		return {
			...prev,
			[key]: value,
		};
	}, {});
}

export function dynamicSanitizer(values) {
	return Object.keys(values).reduce((prev, key) => {
		const value = values[key];

		if(value?.hasOwnProperty?.("value")){
			let finalValue = value.value;
			if(isEmpty(value?.value)){
				finalValue = null;
			}

			return {
				...prev,
				[key]:{
					...value,
					value:finalValue,
				},
			};
		}	//end if(value.hasProperty(value))

		return {
			...prev,
			[key]: value,
		};
	}, {});
}

function defaultSerializer(values, adType, socialState, sanitize=defaultSanitizer){
	let { name, ...socialValues } = values[adType] || {};
	socialValues = sanitize(socialValues);

	return {
		instantAdType: "s",
		adType,
		name,
		socialValues,
	};
}

const defaultDynamicSerializer = makeDynamic(defaultSerializer);
const defaultDynamicCarouselSerializer = makeDynamic(defaultCarouselSerializer);

function makeDynamic(serializer){
	return function(values, adType, socialState, sanitize=defaultSanitizer){
		const serialized = serializer(values, adType, socialState, sanitize);

		const { isDynamic, assetType, ...socialValues } = serialized.socialValues || {};
		const instantAdType = isDynamic ? "d" : "s";

		return {
			...serialized,
			instantAdType,
			assetType,
			isDynamic,
			socialValues,
		};
	};
}

export function defaultCarouselSerializer(values, adType, socialState, sanitize = defaultSanitizer){
	let serialized = defaultSerializer(values, adType, socialState, sanitize);
	/*
		Cloning prevents us from causing problems when removing the clientId, etc.,
		e.g. If you submit and the submit fails because of a concept name collision,
		you don't want the serializer to mutate the data and cause problems with rendering.
	*/
	let { cards } = cloneDeep(serialized.socialValues || {});

	if(cards){
		delete serialized.socialValues?.currentCard;

		cards = cards.map((card) => {
			const sanitizedCard = sanitize(card);
			if(sanitizedCard.type === SOCIAL_FORMATS.IMAGE){
				delete sanitizedCard.video;
			}
			delete sanitizedCard.clientId;
			return sanitizedCard;
		});

		serialized.socialValues.cards = cards;
	}


	return serialized;
}

export function serializeGoogleDemandGenCarousel(values, adType, socialState, sanitize = defaultSanitizer){
	let serialized = defaultCarouselSerializer(values, adType, socialState, sanitize);

	let { cards } = serialized.socialValues || {};
	const { requiredAspectRatios = {} } = serialized.socialValues || {};

	if(cards){
		cards.forEach((card) => {
			card.images = Object.entries(card.images || {})
				.filter(([aspectRatio]) => requiredAspectRatios[aspectRatio]) // Only keep relevant values
				.map(([aspectRatio, image]) => ({ // Add `aspectRatio` to image objects
					...image,
					aspectRatio,
				}));

			if(card.cta){
				const cardLocale = card.language?.id || "en";
				const cardCTA = socialState.googleLocales.locales[cardLocale]?.ctas?.find(cta => cta.name === card.cta);

				card.cta = {
					...cardCTA,
					locale: cardLocale,
				};
			}

			delete card.language;
		});
	}

	serialized.socialValues.cards = cards;
	/*
		requiredAspectRatios is a hash like:
		{
			square: true,
			vertical: false,
		}

		We need to serialize it as an array of just the required ratio names.
		e.g. ["square"]
	*/
	serialized.socialValues.aspectRatios = Object.keys(requiredAspectRatios).filter(ratio => requiredAspectRatios[ratio]);

	delete serialized.socialValues?.currentCard;
	delete serialized.socialValues?.requiredAspectRatios;

	return serialized;
}

export function buildMOCTA(cta){
	let mappedCTA = { ...cta };

	if(cta){
		mappedCTA = {
			...cta,
			name:cta.value,
			id:FACEBOOK_CTA_IDS[cta.value],
		};
	}

	return mappedCTA;
}

export function serializeFacebook(values, adType, socialState, sanitize=defaultSanitizer){
	let serialized = defaultSerializer(values, adType, socialState, sanitize);
	let {
		cta,
		placements,
		cards,
		headline,
		primaryText,
	} = serialized.socialValues || {};

	if(cta){
		let id = FACEBOOK_CTA_IDS[cta];
		if(id){
			serialized.socialValues.cta = {
				id,
				name: cta,
			};
		}
	}

	// Remove end spaces in headline and primaryText
	if (headline) {
		serialized.socialValues.headline = headline.trim();
	}
	if (primaryText) {
		serialized.socialValues.primaryText = primaryText.trim();
	}

	if(placements) {
		const serializedPlacements = Object.values(placements).map((placement) => {
			const serializedPlacement = { ...placement };
			delete serializedPlacement._isNew;

			return sanitize(serializedPlacement);
		});
		if (serializedPlacements.length) {
			serialized.socialValues.placements = serializedPlacements;
		}
	}

	if(cards){
		delete serialized.socialValues?.currentCard;

		// delete seeMore fields if not relevant
		const seeMoreCard = serialized.socialValues?.seeMoreCard;
		if(!seeMoreCard){
			delete serialized.socialValues?.seeMoreDisplayLink;
			delete serialized.socialValues?.seeMoreDisplayUrl;
		}

		cards = cards.map((card) => {
			const sanitizedCard = sanitize(card);
			if(sanitizedCard.type === SOCIAL_FORMATS.IMAGE){
				delete sanitizedCard.video;
			}
			delete sanitizedCard.clientId;
			return sanitizedCard;
		});

		serialized.socialValues.cards = cards;
	}

	delete serialized.socialValues?._currentActivePAC;
	delete serialized.socialValues?.["_select-aspect-ratios"];

	return serialized;
}

export function serializeFacebookMO(values, adType, socialState, sanitize=defaultSanitizer){
	let serialized = defaultDynamicSerializer(values, adType, socialState, sanitize);

	let {
		placements,
		cards,
		headline,
		primaryText,
	} = serialized.socialValues || {};

	// Remove end spaces in headline and primaryText
	if (headline) {
		serialized.socialValues.headline.value = headline.value?.trim() || null;
	}
	if (primaryText) {
		serialized.socialValues.primaryText.value = primaryText.value?.trim() || null;
	}

	serialized.socialValues.cta = buildMOCTA(serialized.socialValues.cta);


	if(placements) {
		const serializedPlacements = Object.values(placements).map((placement) => {
			const serializedPlacement = { ...placement };
			delete serializedPlacement._isNew;

			return sanitize(serializedPlacement);
		});
		if (serializedPlacements.length) {
			serialized.socialValues.placements = serializedPlacements;
		} else {
			serialized.socialValues.placements = [];
		}
	}

	if(cards){
		delete serialized.socialValues?.currentCard;

		// delete seeMore fields if not relevant
		const seeMoreCard = serialized.socialValues?.seeMoreCard;
		if(!seeMoreCard){
			delete serialized.socialValues?.seeMoreDisplayLink;
			delete serialized.socialValues?.seeMoreDisplayUrl;
		}

		cards = cards.map((card) => {
			const sanitizedCard = sanitize(card);
			if(sanitizedCard.type === SOCIAL_FORMATS.IMAGE){
				delete sanitizedCard.video;
			}
			delete sanitizedCard.clientId;
			return sanitizedCard;
		});

		serialized.socialValues.cards = cards;
	}

	delete serialized.socialValues?._currentActivePAC;
	delete serialized.socialValues?.["_select-aspect-ratios"];

	return serialized;
}

export function serializeGoogleDemandGenImage(values, adType, socialState, sanitizer=defaultSanitizer) {
	let serialized = defaultSerializer(values, adType, socialState, sanitizer);
	let {
		headlines,
		descriptions,
		images,
		logos,
		language,
		cta,
	} = serialized.socialValues || {};

	function serializeTextFieldArray(field) {
		let populatedFields = field.filter(val => !isEmpty(val));

		if(populatedFields.length) {
			return populatedFields.map(val => ({ name: val }));
		}

		return null;
	}

	function serializeImageFieldArray(field){
		return field.map((val) => {
			const { name, id } = val;

			return {
				name,
				id,
			};
		});
	}

	if(headlines) {
		serialized.socialValues.headlines = serializeTextFieldArray(headlines);
	}

	if(descriptions) {
		serialized.socialValues.descriptions = serializeTextFieldArray(descriptions);
	}

	if(images) {
		serialized.socialValues.images = serializeImageFieldArray(images);
	}

	if(logos) {
		serialized.socialValues.logos = serializeImageFieldArray(logos);
	}

	if(cta){
		const locale = language?.id || "en";
		const ctaObj = socialState.googleLocales.locales[locale]?.ctas?.find(fullCTA => fullCTA.name === cta);
		serialized.socialValues.cta = {
			...ctaObj,
			locale,
		};

		delete serialized.socialValues.language;
	}

	return serialized;
}

const SERIALIZERS_BY_ADTYPE = { /*
		Sanitization occurs within defaultSerializer. This removes any empty values ("", [], {}, etc) from
		being passed through to the api. When adding a cusotm serializer, make sure to either first pass your
		values through defaultSerializer or sanitize your values via defaultSanitizer or some custom
		sanitization function.
	*/
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE]: serializeFacebook,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO]: serializeFacebook,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: serializeFacebook,
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO]: serializeFacebookMO,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO]: serializeFacebookMO,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: serializeFacebookMO,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL]: serializeGoogleDemandGenCarousel,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE]: serializeGoogleDemandGenImage,
	[SOCIAL_AD_TYPES.PINTEREST_CAROUSEL]: defaultDynamicCarouselSerializer,
	[SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL]: defaultDynamicCarouselSerializer,
};

export function serializeByAdType(values, adType, socialState) {
	const isDynamicType = DYNAMIC_SOCIAL_AD_TYPES.includes(adType);

	const fallbackSerializer = isDynamicType ? defaultDynamicSerializer : defaultSerializer;
	const sanitize = isDynamicType ? dynamicSanitizer : defaultSanitizer;

	const serializer = SERIALIZERS_BY_ADTYPE[adType] || fallbackSerializer;
	return serializer(values, adType, socialState, sanitize);
}
